/**
 * DeleteConfirmSheet — Bottom sheet for confirming batch delete (2+ items).
 *
 * Single deletes use the undo toast pattern (no confirmation).
 * This sheet is ONLY for batch deletes of 2+ items.
 *
 * "Recoverable for 30 days." — references soft delete pattern.
 */

import { T } from '@/styles/tokens';

interface DeleteConfirmSheetProps {
  count: number;
  onConfirm: () => void;
  onClose: () => void;
}

export function DeleteConfirmSheet({ count, onConfirm, onClose }: DeleteConfirmSheetProps) {
  return (
    <>
      <style>{`
        @keyframes dcBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dcSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>

      <div style={{ position: 'fixed', inset: 0, zIndex: 210, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            animation: 'dcBackdropIn 150ms ease',
          }}
        />

        {/* Sheet */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          background: T.white,
          borderRadius: '16px 16px 0 0',
          padding: '16px 20px 32px',
          animation: 'dcSlideUp 250ms cubic-bezier(0.25, 1, 0.5, 1)',
          paddingBottom: 'max(32px, env(safe-area-inset-bottom))',
        }}>
          {/* Drag handle */}
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D4D1CB', margin: '0 auto 16px' }} />
          </div>

          {/* Title */}
          <h3 style={{
            fontSize: 18,
            fontWeight: 700,
            color: T.text,
            margin: '0 0 4px',
            fontFamily: T.fontFamily,
          }}>
            Delete {count} piece{count !== 1 ? 's' : ''}?
          </h3>
          <p style={{
            fontSize: 14,
            color: T.textSecondary,
            margin: '0 0 20px',
            fontFamily: T.fontFamily,
          }}>
            Recoverable for 30 days.
          </p>

          {/* Delete button */}
          <button
            onClick={onConfirm}
            style={{
              width: '100%',
              padding: 16,
              borderRadius: 12,
              background: T.danger,
              color: T.white,
              border: 'none',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: T.fontFamily,
              marginBottom: 12,
            }}
          >
            Delete
          </button>

          {/* Cancel */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 12,
              background: 'transparent',
              color: T.textSecondary,
              border: 'none',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: T.fontFamily,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
